// Content language and translation DTOs. The UI is localized per user; item content is not: every
// product has a content language, items may override it (auto-detected or set by hand) and readers
// can request a machine translation into their own locale.
import { z } from "zod";

export const CONTENT_LANGUAGES = ["en", "de"] as const;
export const ContentLanguage = z.enum(CONTENT_LANGUAGES);
export type ContentLanguage = z.infer<typeof ContentLanguage>;

/** `auto`: set by language detection (may be replaced by a later detection); `manual`: chosen by a person (never overridden). */
export const CONTENT_LANGUAGE_SOURCES = ["auto", "manual"] as const;
export const ContentLanguageSource = z.enum(CONTENT_LANGUAGE_SOURCES);
export type ContentLanguageSource = z.infer<typeof ContentLanguageSource>;

export function isContentLanguage(value: unknown): value is ContentLanguage {
  return value === "en" || value === "de";
}

/**
 * Per-locale text where every locale is optional (type templates). Unlike `Localized` (display names)
 * English is not required: a German-only template is fine.
 */
export const LocalizedText = z.object({ en: z.string().optional(), de: z.string().optional() });
export type LocalizedText = z.infer<typeof LocalizedText>;

/**
 * Picks the variant for `locale`, falling back to English, then to any other non-empty variant.
 * A plain string (legacy, single-language value) is returned as is. Null when there is nothing.
 */
export function pickLocalizedText(value: LocalizedText | string | null | undefined, locale: string): string | null {
  if (value == null) return null;
  if (typeof value === "string") return value;
  const map = value as Record<string, string | undefined>;
  const lang = locale.slice(0, 2).toLowerCase();
  const pick = map[lang] ?? map.en ?? Object.values(map).find((v): v is string => typeof v === "string");
  return pick ?? null;
}

export const TRANSLATION_PROVIDERS = ["gemini", "deepl", "openai"] as const;
export const TranslationProviderId = z.enum(TRANSLATION_PROVIDERS);
export type TranslationProviderId = z.infer<typeof TranslationProviderId>;

/** Where a cached translation came from: one of the server providers or an agent via MCP (`mcp`). */
export type TranslationSource = TranslationProviderId | "mcp";

export const TRANSLATION_JOB_STATES = ["queued", "running", "done", "failed", "waiting_quota"] as const;
export type TranslationJobState = (typeof TRANSLATION_JOB_STATES)[number];

export type TranslationEntityType = "item" | "comment" | "note";

export interface TranslationJobView {
  id: string;
  state: TranslationJobState;
  priority: "interactive" | "bulk";
  /** When a paused provider is expected to accept requests again (state waiting_quota). */
  resumeAt: string | null;
  attempts: number;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * GET /items/:ref/translation and /comments/:id/translation.
 * - `fresh`: a translation of the current source text exists.
 * - `stale`: only a translation of an older version exists (shown with a hint while a new one is made).
 * - `none`: nothing cached.
 * `job` is the newest job for the current source text, if any.
 */
export interface TranslationStatusView {
  entityType: TranslationEntityType;
  /** Items and comments; null for notes. */
  itemId: string | null;
  commentId: string | null;
  noteId: string | null;
  targetLocale: ContentLanguage;
  /** Language the content is written in (item override, else product), null for comments without a detected language. */
  sourceLanguage: ContentLanguage | null;
  /** True when the content already is in the target language (nothing to translate). */
  sameLanguage: boolean;
  /** Hash of the current source text; `set_translation` must quote it. */
  sourceHash: string;
  status: "fresh" | "stale" | "none";
  translation: {
    title: string | null;
    descriptionMd: string | null;
    bodyMd: string | null;
    provider: TranslationSource;
    model: string | null;
    sourceHash: string;
    createdAt: string;
  } | null;
  job: TranslationJobView | null;
  /** Translation is switched on and at least one server provider is configured (else only agents can fill the cache). */
  providerAvailable: boolean;
}

/** Payload of `translation.updated` (product-scoped): a translation of the entity was stored. */
export interface TranslationUpdatedPayload {
  entityType: TranslationEntityType;
  itemId: string | null;
  commentId: string | null;
  noteId: string | null;
  targetLocale: ContentLanguage;
  sourceHash: string;
  provider: TranslationSource;
  /** Translated title (items only; null for comments), so clients can patch their title caches without a refetch. */
  title: string | null;
  /** The stored translation belongs to the current text of the entity (false: it is already outdated). */
  fresh: boolean;
}

/** One entry of `GET /translations/titles`: an item's title in the requested locale. */
export interface TranslatedTitle {
  title: string;
  /** `stale`: made from an older version of the item; still shown, with a hint, until a new one arrives. */
  status: "fresh" | "stale";
  provider: TranslationSource;
}

/** Response of `GET /translations/titles?locale=de[&products=<id>,<id>]`: item id -> translated title. */
export type TranslatedTitles = Record<string, TranslatedTitle>;

/** Payload of `translation.job` (delivered only to `userIds`, the people who asked for it). */
export interface TranslationJobPayload {
  userIds: string[];
  jobId: string;
  entityType: TranslationEntityType;
  itemId: string | null;
  commentId: string | null;
  noteId: string | null;
  targetLocale: ContentLanguage;
  state: TranslationJobState;
  resumeAt: string | null;
  error: string | null;
}
